/**
 * PluginRuntime — ponte estática entre o app e os plugins/PluginWrap.
 *
 * O app (host) injeta uma vez o contexto da sessão já autenticada (socket
 * conectado, companyId, user) via `PluginRuntime.configure(...)`. Os plugins e
 * o PluginWrap consomem sem conhecer AuthContext nem abrir socket próprio —
 * reusam o socket autenticado do app (nada público).
 *
 * Mesmo padrão de `setModalOpener` (o host injeta a capability, o plugin usa).
 */

import { setPluginDebug, isPluginDebug } from "./debug";

type SocketLike = {
  on(event: string, handler: (...args: any[]) => void): void;
  off(event: string, handler: (...args: any[]) => void): void;
} | null;

interface RuntimeConfig {
  socket?: SocketLike;
  companyId?: number | null;
  user?: any;
  /**
   * Idioma da INTERFACE agora ("pt", "en", "es"...). Vem do i18next do app —
   * do que o usuário escolheu no menu ou do navegador —, não do banco: não há
   * coluna de idioma no usuário. Atualiza sozinho quando o idioma muda, e o
   * runtime emite "locale:change" junto.
   */
  idioma?: string;
  /** Config/preferências do usuário logado. */
  userSettings?: any;
  /** Conexões (whatsApps) da empresa. */
  connections?: any[];
  /** Config da empresa. */
  companySettings?: any;
  /** React/ReactDOM do app — pra plugins montarem componentes React reais. */
  react?: any;
  reactDOM?: any;
  /**
   * Tema do Material-UI do app.
   *
   * Sem ele, o JSX de plugin renderiza numa arvore React PROPRIA, fora do
   * ThemeProvider — e o MUI v4, cujas classes sao GLOBAIS
   * (`.MuiButton-containedPrimary`), injeta uma segunda folha com o tema
   * PADRAO. Por ser injetada depois, ela ganha na pagina inteira: medido em
   * 29-10-2026, o botao do proprio app virou de rgb(0,107,118) para o indigo
   * rgb(63,81,181) no instante em que um modal de plugin montou.
   *
   * Com o tema aqui, o motor envolve o JSX no ThemeProvider do app: um tema,
   * uma folha, e o plugin sai com a cor do sistema em vez da do MUI.
   */
  theme?: any;
  /** Namespace do Material-UI do app (@material-ui/core). */
  mui?: any;
  /**
   * Cliente HTTP do app (axios com baseURL, Bearer e refresh de token já
   * resolvidos). É como a tela de um plugin lê e grava dado do sistema em nome
   * do usuário logado — sujeito às permissões dele, sem privilégio extra.
   *
   * Tipado como `any` de propósito: o SDK não depende de axios.
   */
  api?: any;
  /** Quaisquer outros dados de sessão que o app queira expor (via get). */
  [k: string]: any;
}

/**
 * Eventos que o app emite e os plugins assinam.
 *
 * Lista PROPRIA, e nao a do catalogo de callbacks (events/names.ts): aqui o
 * canal e o socket do app para a UI do plugin, dentro do navegador. Os nomes
 * chegam a coincidir — `ticket:updated` existe nos dois — significando coisas
 * diferentes: la e "avise o sistema do cliente", aqui e "a tela mudou".
 *
 * Declarados como constante porque quem EMITE e o app (layout/index.js) e quem
 * ASSINA e o plugin: eram duas pontas digitando a mesma string na mao.
 */
export const RUNTIME_EVENTS = {
  /** Mensagem nova num ticket. */
  TICKET_MESSAGE: "ticket:message",
  /** Ticket alterado (status, fila, atribuicao...). */
  TICKET_UPDATED: "ticket:updated",
  CONTACT_UPDATED: "contact:updated",
  CONNECTION_STATUS: "connection:status",
  /** O ticket aberto no chat mudou (store reativo). */
  ACTIVE_TICKET_CHANGE: "active-ticket:change"
} as const;

/** Para iterar/validar em runtime. */
export const RUNTIME_EVENT_LIST = Object.values(RUNTIME_EVENTS);

/** So os nomes que o app emite hoje. */
export type KnownRuntimeEvent = (typeof RUNTIME_EVENTS)[keyof typeof RUNTIME_EVENTS];

/**
 * Nome de evento do runtime.
 *
 * O `(string & {})` no lugar de `string` e proposital: com `| string` puro o
 * TypeScript colapsa o union inteiro para `string` e o autocomplete some — os
 * nomes acima viravam comentario. Assim o editor sugere os conhecidos e o app
 * segue livre para emitir um evento proprio.
 */
export type RuntimeEvent = KnownRuntimeEvent | (string & {});

/** Ações de alto nível que SÓ o front sabe executar (registradas pelo app). */
export interface RuntimeActions {
  /** Abre/foca um ticket na UI. */
  openTicket?: (ticketId: number | string) => void;
  /** Navega para uma rota do app. */
  navigate?: (path: string) => void;
  /** Pede refresh de alguma área da UI (ex: "tickets", "contacts"). */
  refresh?: (area?: string) => void;
  /** Extensível: o app pode registrar outras ações. */
  [name: string]: ((...args: any[]) => any) | undefined;
}

/**
 * Store central do contexto da sessão, exposto aos plugins. O app alimenta via
 * configure()/setActiveTicket(); plugins leem (PluginRuntime.user, .companyId,
 * .activeTicket, .get("x")). Evita cada plugin/slot puxar de AuthContext e
 * centraliza o que pode ser reusado (usuário, empresa, ticket aberto, config).
 */
class PluginRuntimeClass {
  private socket: SocketLike = null;
  companyId: number | null = null;
  user: any = null;
  /** Idioma da interface agora ("pt", "en", "es"…). Ver RuntimeConfig.idioma. */
  idioma: string | null = null;
  userSettings: any = null;
  /** Ticket atualmente aberto no chat (setado pelo app ao abrir). */
  activeTicket: any = null;
  /** Conexões (whatsApps) e config da empresa (alimentadas via configure). */
  connections: any[] = [];
  companySettings: any = null;
  /** React/ReactDOM DO APP — permitem ao plugin montar componentes React reais
   *  (mesma instância, sem bundle próprio). Acesse via PluginRuntime.react. */
  react: any = null;
  reactDOM: any = null;
  /**
   * Material-UI DO APP.
   *
   * Sem isto, tela de plugin em JSX so tem `div` cru: sai fora do tema, com
   * botao que nao parece botao do sistema. Era o que faltava para o plugin
   * poder desenhar a PROPRIA tela em vez de o core inventar um tipo de campo
   * novo a cada necessidade (foi assim que nasceu o `picker`).
   *
   * E o mesmo pacote que o app usa — o plugin nao carrega bundle proprio e
   * herda tema, modo escuro e tipografia de graca.
   */
  mui: any = null;
  /** Tema do MUI do app — ver RuntimeConfig.theme. */
  theme: any = null;
  /**
   * Cliente HTTP DO APP (axios com baseURL, Bearer e refresh já resolvidos).
   *
   * É como a tela de um plugin lê e grava dado do sistema em nome do usuário
   * logado. Campo de primeira classe, e não item do `store`: sem estar na lista
   * `known` do configure(), ele só sairia por `PluginRuntime.get("api")`, e
   * quem escrevesse o natural — `markedeskRuntime.api` — receberia `undefined`
   * sem erro nenhum.
   */
  api: any = null;
  /** Ações de alto nível registradas pelo app (frente híbrida). */
  actions: RuntimeActions = {};
  /** Saco genérico para dados extras de sessão. */
  private store: Record<string, any> = {};
  /** Event bus: nome do evento → listeners. */
  private listeners: Map<string, Set<(payload: any) => void>> = new Map();

  /** O app injeta/atualiza o contexto da sessão (idempotente, merge raso). */
  configure(cfg: RuntimeConfig): void {
    const known = ["socket", "companyId", "user", "idioma", "userSettings", "connections", "companySettings", "react", "reactDOM", "mui", "theme", "api"];
    if ("socket" in cfg) this.socket = cfg.socket || null;
    if ("companyId" in cfg) this.companyId = cfg.companyId ?? null;
    if ("user" in cfg) this.user = cfg.user ?? null;
    if ("idioma" in cfg) this.idioma = cfg.idioma ?? null;
    if ("userSettings" in cfg) this.userSettings = cfg.userSettings ?? null;
    if ("connections" in cfg) this.connections = cfg.connections || [];
    if ("companySettings" in cfg) this.companySettings = cfg.companySettings ?? null;
    if ("react" in cfg) this.react = cfg.react || null;
    if ("reactDOM" in cfg) this.reactDOM = cfg.reactDOM || null;
    if ("mui" in cfg) this.mui = cfg.mui || null;
    if ("theme" in cfg) this.theme = cfg.theme || null;
    if ("api" in cfg) this.api = cfg.api || null;
    for (const k of Object.keys(cfg)) {
      if (!known.includes(k)) this.store[k] = cfg[k];
    }
  }

  /** O app registra ações de alto nível (merge — pode chamar várias vezes). */
  registerActions(actions: RuntimeActions): void {
    this.actions = { ...this.actions, ...actions };
  }

  /** Define o ticket aberto no chat; emite "active-ticket:change". */
  setActiveTicket(ticket: any): void {
    this.activeTicket = ticket || null;
    this.emit(RUNTIME_EVENTS.ACTIVE_TICKET_CHANGE, this.activeTicket);
  }

  /** Lê um dado extra do store por chave. */
  get(key: string): any { return this.store[key]; }

  // ── Event bus ──────────────────────────────────────────────────────────

  /** Assina um evento do runtime. Retorna unsubscribe. */
  on(event: RuntimeEvent, cb: (payload: any) => void): () => void {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set());
    const set = this.listeners.get(event)!;
    set.add(cb);
    return () => { set.delete(cb); };
  }

  /** O app (host) emite um evento para os plugins. */
  emit(event: RuntimeEvent, payload?: any): void {
    const set = this.listeners.get(event);
    if (!set) return;
    for (const cb of set) { try { cb(payload); } catch { /* noop */ } }
  }

  /**
   * Atalho: assina mensagens novas de um ticket específico. Reusa o socket
   * autenticado do app via o evento "ticket:message" (emitido pelo host quando
   * chega company-{companyId}-appMessage). Retorna unsubscribe.
   */
  onTicketMessage(ticketId: number | string, cb: (payload?: any) => void): () => void {
    return this.on(RUNTIME_EVENTS.TICKET_MESSAGE, (payload: any) => {
      const tId = payload && (payload.ticketId ?? payload.ticket?.id ?? payload.message?.ticketId);
      if (String(tId) === String(ticketId)) {
        // O payload é o do socket (`{ action, message, ticket, contact }`):
        // quem assina precisa saber, por exemplo, se a mensagem é do
        // atendente (`message.fromMe`) para não reagir a ela (#4327).
        try { cb(payload); } catch { /* noop */ }
      }
    });
  }

  /** Liga/desliga os logs de debug do sistema de plugins (persiste). */
  setDebug(on: boolean): void { setPluginDebug(on); }

  /** Estado atual do debug. */
  isDebug(): boolean { return isPluginDebug(); }
}

/** Singleton compartilhado (app configura, plugins consomem). */
export const PluginRuntime = new PluginRuntimeClass();

// Expõe no global para acesso via console do navegador:
//   window.markedeskRuntime.setDebug(true)
try {
  if (typeof globalThis !== "undefined") (globalThis as any).markedeskRuntime = PluginRuntime;
} catch { /* noop */ }
